import appSettings from 'wanderu-config';
import { i18nMoment } from 'wanderu-i18n';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import {
    TixService,
    WapiService,
} from 'wanderu-service-wapi';
import { isLocalStorageAvailable } from 'wanderu-utils';

import {
    CognitoUnableToAuthenticateUserError,
    UnableToAutoAuthenticateUserError,
    LoginFailedUseThirdPartyError,
} from './Errors';
import TicketService from './TicketService';
import CognitoService from './cognito';
import {
    USER_NOT_FOUND,
    FEDERATED_TYPE,
    COGNITO_PROVIDER,
    FACEBOOK_PROVIDER,
    GOOGLE_PROVIDER,
    LOCAL_STORAGE_KEY,
} from './constants';
import Events from './events/AccountServiceEvents';
import FacebookService from './facebook';
import {
    GoogleButtonService,
    GoogleOneTapService,
} from './google';
import {
    clearLocalStorageCredentials,
    formatUserInfo,
    formatUserInfoForPost,
} from './utils';

const logger = Logger.getLogger('AccountService');

function checkAuthTokenValidity(authTokenExp) {
    return i18nMoment(authTokenExp).format() > i18nMoment().format();
}

async function saveUserInfo(userInfo) {
    try {
        await WapiService.post('userinfo.json', {
            body: {
                userInfo: formatUserInfoForPost(userInfo),
            },
        });

        const updatedUser = await getUserInfo();

        return updatedUser.userInfo;
    } catch (ex) {
        logger.error('saveUserInfo', ex);
        throw ex;
    }
}

async function getUserInfo() {
    const { result = {} } = await WapiService.get('userinfo.json');

    return formatUserInfo(result);
}

function getLoggedInState() {
    if (!isLocalStorageAvailable()) {
        return undefined;
    }

    const loggedInState = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_KEY)) || {};

    const isLoggedIn = checkAuthTokenValidity(loggedInState.authTokenExp);

    return {
        isCognito: loggedInState.provider === COGNITO_PROVIDER,
        isFacebook: loggedInState.provider === 'facebook',
        isGoogle: loggedInState.provider === 'google',
        isLoggedIn,
        ...loggedInState,
    };
}

function setLoggedInState(authTokens) {
    if (!isLocalStorageAvailable()) {
        return;
    }

    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(authTokens));
}

async function onSuccessfulLogin({
    email,
    accessToken,
    authToken,
    authTokenExp,
    identityId,
    sessionId,
    getLoggedInUser = () => { },
    loginInteraction = () => { },
    provider,
    method,
}) {
    await WapiService.restoreSession({
        accessToken,
        authToken,
        email,
        provider,
        sessionId,
    });
    let {
        userInfo,
        loyaltyInfo,
    } = await getUserInfo();

    setLoggedInState({
        accessToken,
        authToken,
        authTokenExp,
        email: userInfo?.email,
        identityId,
        provider,
        sessionId,
    });

    SnowplowService.setUserId(identityId);

    const { firstName, lastName } = userInfo || {};

    window?.DD_RUM?.setUser({
        email: userInfo?.email || email,
        id: identityId,
        name: (firstName && lastName)
            ? `${firstName} ${lastName}`
            : undefined,
    });

    const interaction = loginInteraction();
    let newAccount = false;

    if (!userInfo.email) {
        newAccount = true;
        let success = true;

        try {
            const user = await getLoggedInUser();
            userInfo = await saveUserInfo(user);
        } catch (ex) {
            success = false;
            throw ex;
        } finally {
            const signUpEvent = Events.createSignUpEvent({
                email: userInfo?.email,
                interaction,
                method,
                provider,
                success,
            });
            SnowplowService.fireStructuredEvent(signUpEvent);
        }
    }

    return {
        identityId,
        loyaltyInfo,
        newAccount,
        userInfo,
    };
}

async function logInWithCognito(email, password) {
    let success = true;
    let userData;

    try {
        const provider = COGNITO_PROVIDER;
        const result = await CognitoService.logIn(email, password, provider);

        const {
            token: authToken,
            identityId,
            sessionId,
            expDt: authTokenExp,
        } = result;

        userData = await onSuccessfulLogin({
            authToken,
            authTokenExp,
            email,
            identityId,
            provider,
            sessionId,
        });

        return userData;
    } catch (ex) {
        if (ex.name === USER_NOT_FOUND) {
            logger.info('logIn', ex);
        } else {
            logger.warn('logIn', ex);
        }

        success = false;

        if (ex.constructor === LoginFailedUseThirdPartyError) {
            throw ex;
        }

        throw new CognitoUnableToAuthenticateUserError();
    } finally {
        const cognitoLoginEvent = Events.createLogInEvent({
            email,
            provider: 'cognito',
            success,
        });

        SnowplowService.fireStructuredEvent(cognitoLoginEvent);
    }
}

async function federatedLogIn(token, provider) {
    return WapiService.postWithoutAuth('auth.json', {
        body: {
            credentials: {
                ...appSettings.config.wapi.credentials,
                provider,
                token,
                type: FEDERATED_TYPE,
            },
        },
    });
}

async function logInWithFacebook() {
    let success = true;
    let userData;

    try {
        const accessToken = await FacebookService.logIn();
        const provider = FACEBOOK_PROVIDER;

        const { result } = await federatedLogIn(accessToken, provider);

        const {
            token: authToken,
            identityId,
            sessionId,
            expDt: authTokenExp,
        } = result;

        userData = await onSuccessfulLogin({
            accessToken,
            authToken,
            authTokenExp,
            getLoggedInUser: FacebookService.getLoggedInUser,
            identityId,
            provider,
            sessionId,
        });

        return userData;
    } catch (ex) {
        success = false;
        throw ex;
    } finally {
        const facebookLoginEvent = Events.createLogInEvent({
            email: userData?.userInfo?.email,
            provider: FACEBOOK_PROVIDER,
            success,
        });
        SnowplowService.fireStructuredEvent(facebookLoginEvent);
    }
}

async function logInWithGoogleButton(response) {
    let success = true;
    let userData;

    try {
        const accessToken = response?.credential;
        GoogleButtonService.logIn(accessToken);

        const provider = GOOGLE_PROVIDER;

        const { result } = await federatedLogIn(accessToken, provider);

        const {
            token: authToken,
            identityId,
            sessionId,
            expDt: authTokenExp,
        } = result;

        userData = await onSuccessfulLogin({
            accessToken,
            authToken,
            authTokenExp,
            getLoggedInUser: GoogleButtonService.getLoggedInUser,
            identityId,
            method: 'button',
            provider,
            sessionId,
        });

        return userData;
    } catch (ex) {
        success = false;
        throw ex;
    } finally {
        const googleLoginEvent = Events.createLogInEvent({
            email: userData?.userInfo?.email,
            method: 'button',
            provider: 'google',
            success,
        });
        SnowplowService.fireStructuredEvent(googleLoginEvent);
    }
}

function postLogOutCleanUp() {
    SnowplowService.removeUserId();
    window?.DD_RUM?.clearUser();
    WapiService.clearCredentials();
    clearLocalStorageCredentials();
}

async function globalLogOut() {
    postLogOutCleanUp();
}

/**
 * Signs the current user out.
 */
async function logOut() {
    const loggedInState = await getLoggedInState();

    if (!loggedInState.isLoggedIn) {
        return;
    }

    if (loggedInState.isFacebook) {
        await FacebookService.logOut();
    } else if (loggedInState.isGoogle) {
        await Promise.all([
            GoogleOneTapService.logOut(),
            GoogleButtonService.logOut(),
        ]);
    } else if (loggedInState.isCognito) {
        WapiService.post('logout.json', { body: {} });
    }

    postLogOutCleanUp();
}

/**
 * Refresh the auth token to allow a previously logged in user with an expired token to
 * log in automatically again
 * @param email The user's email address
 * @param token The expired auth token
 */
async function refreshToken(email, token) {
    const { result } = await WapiService.post('refresh.json', {
        body: {
            email,
            token,
        },
    });

    const {
        token: authToken,
        identityId,
        sessionId,
        expDt: authTokenExp,
    } = result;

    return onSuccessfulLogin({
        authToken,
        authTokenExp,
        email,
        identityId,
        provider: COGNITO_PROVIDER,
        sessionId,
    });
}

async function getTrips(locale) {
    const response = await TixService.get(`v3/trips?locale=${locale}`);
    return response.result.orders;
}

async function checkCancellation(orderId) {
    const response = await TixService.get(`v3/trips/cancel/check?order_id=${orderId}`);
    return response.result;
}

async function cancelTrip(orderId) {
    const response = await TixService.post('v3/trips/cancel', {
        body: {
            order_id: orderId,
        },
    });

    return response.result;
}

async function federatedAutoLogIn({
    token,
    provider,
    method,
    getLoggedInUser = () => { },

}) {
    const { result } = await federatedLogIn(token, provider);

    const {
        token: authToken,
        identityId,
        sessionId,
        expDt: authTokenExp,
    } = result;

    const userData = await onSuccessfulLogin({
        authToken,
        authTokenExp,
        getLoggedInUser,
        identityId,
        provider,
        sessionId,
    });

    const {
        userInfo,
        newAccount,
    } = userData;

    if (newAccount) {
        return userData;
    }

    const loginEvent = Events.createLogInEvent({
        auto: true,
        email: userInfo?.email,
        method,
        provider,
        success: true,
    });

    SnowplowService.fireStructuredEvent(loginEvent);

    return userData;
}

async function cognitoAutoLogIn() {
    const {
        email,
        authToken,
        sessionId,
        identityId,
        provider,
        authTokenExp,
    } = getLoggedInState();

    const isLoggedIn = checkAuthTokenValidity(authTokenExp);

    let userData;

    if (!isLoggedIn) {
        userData = await refreshToken(email, authToken);
    }

    if (isLoggedIn) {
        userData = await onSuccessfulLogin({
            authToken,
            authTokenExp,
            email,
            identityId,
            provider,
            sessionId,
        });
    }

    const cognitoLoginEvent = Events.createLogInEvent({
        auto: true,
        email: userData?.userInfo?.email,
        success: true,
    });
    SnowplowService.fireStructuredEvent(cognitoLoginEvent);

    return userData;
}

async function performAutoLogin() {
    try {
        const { isCognito } = getLoggedInState();

        if (isCognito) {
            return cognitoAutoLogIn();
        }

        const facebookLoginState = await FacebookService.getLoggedInState();
        if (facebookLoginState.isLoggedIn) {
            const { accessToken } = facebookLoginState;

            return federatedAutoLogIn({
                getLoggedInUser: FacebookService.getLoggedInUser,
                method: 'button',
                provider: FACEBOOK_PROVIDER,
                token: accessToken,
            });
        }

        const googleLoginState = await GoogleButtonService.getLoggedInState();
        if (googleLoginState.isLoggedIn) {
            const { accessToken } = googleLoginState;

            return federatedAutoLogIn({
                getLoggedInUser: GoogleButtonService.getLoggedInUser,
                method: 'button',
                provider: GOOGLE_PROVIDER,
                token: accessToken,
            });
        }

        const googleAccessToken = await GoogleOneTapService.logIn();
        if (googleAccessToken) {
            return federatedAutoLogIn({
                getLoggedInUser: GoogleOneTapService.getLoggedInUser,
                loginInteraction: GoogleOneTapService.getUserInteraction,
                method: 'one-tap',
                provider: GOOGLE_PROVIDER,
                token: googleAccessToken,
            });
        }

        const autoLoginEvent = Events.createLogInEvent({
            auto: true,
            provider: 'all',
            success: false,
        });
        SnowplowService.fireStructuredEvent(autoLoginEvent);

        throw new UnableToAutoAuthenticateUserError();
    } catch (ex) {
        throw new UnableToAutoAuthenticateUserError();
    }
}

async function fetchTicketUrl(urlForPollingTicket, orderId, ticketType) {
    return TicketService.fetchTicketUrl(urlForPollingTicket, orderId, ticketType);
}

/* cognito service functions */
async function accountExistsForEmail(email) {
    return CognitoService.accountExistsForEmail(email);
}

async function changePassword(currentPassword, newPassword) {
    return CognitoService.changePassword(currentPassword, newPassword);
}

async function createAccount(createAccountInfo) {
    return CognitoService.createAccount(createAccountInfo);
}

async function forgotPassword(email) {
    return CognitoService.forgotPassword(email);
}

async function forgotPasswordConfirmation(email, code, password) {
    return CognitoService.forgotPasswordConfirmation(email, code, password);
}

const accountService = {
    accountExistsForEmail,
    cancelTrip,
    changePassword,
    checkCancellation,
    createAccount,
    fetchTicketUrl,
    forgotPassword,
    forgotPasswordConfirmation,
    getLoggedInState,
    getTrips,
    globalLogOut,
    logInWithCognito,
    logInWithFacebook,
    logInWithGoogleButton,
    logOut,
    performAutoLogin,
    saveUserInfo,
};

export default accountService;
